/**
 * ============================================================================
 * LISTOVERVIEW.JS v4.0 - PLANIVIO DESIGN
 * ============================================================================
 * 
 * ✅ v4.0: Planivio Branding
 *          - Orange → Teal (#4A90A4) / Green (#6BA893)
 *          - Konsistente Farben mit App.js v43
 *          - Akzentfarbe: #E8976C (Warm Orange)
 * ✅ v3.0: Orange Karten-Borders
 * 
 * @version 4.0.0 - Planivio Design
 */

import React from 'react';

const ListOverview = ({ lists = [], onSelectList, onDeleteList, viewMode = 'grid' }) => {

  const safeLists = Array.isArray(lists) ? lists : [];
  
  // ============================================
  // PLANIVIO COLORS
  // ============================================
  const colors = {
    primary: '#4A90A4',      // Warm Blue
    secondary: '#6BA893',    // Soft Green
    accent: '#E8976C',       // Warm Orange
    textPrimary: '#2D3E50',  // Dark text
    textSecondary: '#5A6B7A',
    border: '#B8D4DC',       // Light teal border
    borderLight: '#D4E8EC',  // Very light border
    bgLight: '#E8F4F8',      // Light background
    bgCard: '#F0F9FF',       // Card stat background
    success: '#22C55E',
    error: '#EF4444'
  };
  
  // ============================================
  // HELPERS
  // ============================================
  
  const formatDate = (dateString) => {
    if (!dateString) return null;
    
    try {
      const date = new Date(dateString);
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      if (date.toDateString() === today.toDateString()) {
        return '🎯 Heute';
      } else if (date.toDateString() === tomorrow.toDateString()) {
        return '📅 Morgen';
      } else {
        return date.toLocaleDateString('de-DE', { 
          weekday: 'short', 
          day: '2-digit', 
          month: '2-digit' 
        });
      }
    } catch {
      return null;
    }
  };

  const getPersonName = (assignedTo) => {
    const persons = {
      all: 'Alle',
      alle: 'Alle',
      me: 'Ich',
      partner: 'Partner',
      kids: 'Kinder'
    };
    return persons[assignedTo] || assignedTo || 'Alle';
  };

  const getPriorityInfo = (priority) => {
    const info = {
      high: { label: 'HOCH', color: '#EF4444', bg: '#FEE2E2' },
      normal: { label: 'NORMAL', color: '#6B7280', bg: '#F3F4F6' },
      low: { label: 'NIEDRIG', color: '#22C55E', bg: '#D1FAE5' }
    };
    return info[priority] || info.normal;
  };

  const getStats = (list) => {
    const items = Array.isArray(list?.items) ? list.items : [];
    const total = items.length;
    const checked = items.filter(item => item?.checked === true).length;
    const unchecked = total - checked;
    const progress = total > 0 ? Math.round((checked / total) * 100) : 0;
    
    return { total, checked, unchecked, progress };
  };

  // ============================================
  // STYLES - PLANIVIO DESIGN
  // ============================================
  
  const styles = {
    viewToggle: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '1rem',
      background: 'white',
      border: `2px solid ${colors.border}`,
      padding: '0.25rem',
      borderRadius: '12px',
      width: 'fit-content'
    },
    toggleBtn: {
      padding: '0.5rem 1rem',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '0.875rem',
      fontWeight: 600,
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    toggleBtnActive: {
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
      color: 'white'
    },
    toggleBtnInactive: {
      background: 'transparent',
      color: '#6B7280'
    },
    
    // Grid View
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gap: '1rem'
    },
    card: {
      background: 'white',
      border: `2px solid ${colors.border}`,
      borderRadius: '16px',
      padding: '1.25rem',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '0.75rem'
    },
    cardTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '1.1rem',
      fontWeight: 700,
      color: colors.textPrimary,
      margin: 0
    },
    cardIcon: {
      width: '32px',
      height: '32px',
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1rem'
    },
    deleteBtn: {
      padding: '0.5rem',
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      fontSize: '1.1rem',
      opacity: 0.5,
      transition: 'all 0.2s',
      borderRadius: '8px'
    },
    cardMeta: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap',
      marginBottom: '1rem'
    },
    metaBadge: {
      padding: '0.25rem 0.5rem',
      borderRadius: '6px',
      fontSize: '0.75rem',
      fontWeight: 600,
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem'
    },
    statsRow: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '0.75rem'
    },
    statBox: {
      flex: 1,
      padding: '0.75rem',
      background: colors.bgCard,
      borderRadius: '10px',
      textAlign: 'center',
      border: `1px solid ${colors.borderLight}`
    },
    statNumber: {
      fontSize: '1.5rem',
      fontWeight: 700,
      display: 'block',
      lineHeight: 1.2
    },
    statLabel: {
      fontSize: '0.7rem',
      color: '#6B7280',
      textTransform: 'uppercase',
      fontWeight: 600
    },
    progressContainer: {
      marginTop: '0.5rem'
    },
    progressBar: {
      height: '8px',
      background: colors.borderLight,
      borderRadius: '4px',
      overflow: 'hidden'
    },
    progressFill: {
      height: '100%',
      borderRadius: '4px',
      transition: 'width 0.3s'
    },
    progressText: {
      fontSize: '0.75rem',
      color: '#6B7280',
      marginTop: '0.25rem',
      textAlign: 'center'
    },
    
    // List View
    listContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem'
    },
    listItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      padding: '1rem 1.25rem',
      background: 'white',
      border: `2px solid ${colors.border}`,
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    listIcon: {
      width: '40px',
      height: '40px',
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.25rem',
      flexShrink: 0
    },
    listContent: {
      flex: 1,
      minWidth: 0
    },
    listTitle: {
      fontSize: '1rem',
      fontWeight: 700,
      color: colors.textPrimary,
      margin: 0
    },
    listMeta: {
      fontSize: '0.8rem',
      color: '#6B7280',
      marginTop: '0.25rem',
      display: 'flex',
      gap: '0.75rem',
      flexWrap: 'wrap'
    },
    listStats: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      flexShrink: 0
    },
    listStat: {
      textAlign: 'center',
      minWidth: '45px'
    },
    listStatNumber: {
      fontSize: '1.25rem',
      fontWeight: 700,
      display: 'block',
      lineHeight: 1.2
    },
    listStatLabel: {
      fontSize: '0.65rem',
      color: '#9CA3AF',
      textTransform: 'uppercase'
    },
    
    // Empty State
    emptyState: {
      textAlign: 'center',
      padding: '3rem 1rem',
      background: 'white',
      border: `2px solid ${colors.border}`,
      borderRadius: '16px'
    },
    emptyIcon: {
      fontSize: '4rem',
      marginBottom: '1rem',
      opacity: 0.5
    }
  };

  // ============================================
  // RENDER: EMPTY STATE
  // ============================================
  
  if (safeLists.length === 0) {
    return (
      <div style={styles.emptyState}>
        <div style={styles.emptyIcon}>🛒</div>
        <h3 style={{ margin: '0 0 0.5rem 0', color: colors.textPrimary }}>
          Keine Einkaufslisten vorhanden
        </h3>
        <p style={{ margin: 0, color: '#6B7280' }}>
          Erstelle deine erste Liste mit dem Button oben!
        </p>
      </div>
    );
  }

  // ============================================
  // RENDER: GRID VIEW
  // ============================================
  
  const renderGridView = () => (
    <div style={styles.grid}>
      {safeLists.map((list) => {
        if (!list?.id) return null;
        
        const stats = getStats(list);
        const priority = getPriorityInfo(list.priority);
        
        return (
          <div
            key={list.id}
            style={styles.card}
            onClick={() => onSelectList?.(list)}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = `0 8px 25px rgba(74, 144, 164, 0.2)`;
              e.currentTarget.style.borderColor = colors.primary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.borderColor = colors.border;
            }}
          >
            {/* Header */}
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>
                <span style={styles.cardIcon}>🛒</span>
                {list.name || 'Unbenannte Liste'}
              </h3>
              <button
                style={styles.deleteBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm(`Liste "${list.name}" löschen?`)) {
                    onDeleteList?.(list.id);
                  }
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '1';
                  e.currentTarget.style.background = '#FEE2E2';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '0.5';
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                🗑️
              </button>
            </div>
            
            {/* Meta Badges */}
            <div style={styles.cardMeta}>
              <span style={{
                ...styles.metaBadge,
                background: '#F3F4F6',
                color: '#4B5563'
              }}>
                👤 {getPersonName(list.assignedTo)}
              </span>
              <span style={{
                ...styles.metaBadge,
                background: priority.bg,
                color: priority.color
              }}>
                {priority.label}
              </span>
              {list.dueDate && (
                <span style={{
                  ...styles.metaBadge,
                  background: colors.bgLight,
                  color: colors.primary
                }}>
                  {formatDate(list.dueDate)}
                </span>
              )}
            </div>
            
            {/* Stats */}
            <div style={styles.statsRow}>
              <div style={styles.statBox}>
                <span style={{...styles.statNumber, color: colors.primary}}>
                  {stats.total}
                </span>
                <span style={styles.statLabel}>Gesamt</span>
              </div>
              <div style={styles.statBox}>
                <span style={{...styles.statNumber, color: colors.accent}}>
                  {stats.unchecked}
                </span>
                <span style={styles.statLabel}>Offen</span>
              </div>
              <div style={styles.statBox}>
                <span style={{...styles.statNumber, color: colors.success}}>
                  {stats.checked}
                </span>
                <span style={styles.statLabel}>Erledigt</span>
              </div>
            </div>
            
            {/* Progress */}
            <div style={styles.progressContainer}>
              <div style={styles.progressBar}>
                <div style={{
                  ...styles.progressFill,
                  width: `${stats.progress}%`,
                  background: stats.progress === 100 
                    ? `linear-gradient(90deg, ${colors.success}, #16A34A)`
                    : `linear-gradient(90deg, ${colors.primary}, ${colors.secondary})`
                }} />
              </div>
              <div style={styles.progressText}>
                {stats.progress}% erledigt
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  // ============================================
  // RENDER: LIST VIEW
  // ============================================
  
  const renderListView = () => (
    <div style={styles.listContainer}>
      {safeLists.map((list) => {
        if (!list?.id) return null;
        
        const stats = getStats(list);
        const priority = getPriorityInfo(list.priority);
        
        return (
          <div
            key={list.id}
            style={styles.listItem}
            onClick={() => onSelectList?.(list)}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = colors.primary;
              e.currentTarget.style.transform = 'translateX(4px)';
              e.currentTarget.style.boxShadow = `0 4px 12px rgba(74, 144, 164, 0.15)`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = colors.border;
              e.currentTarget.style.transform = 'translateX(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={styles.listIcon}>🛒</div>
            
            <div style={styles.listContent}>
              <h3 style={styles.listTitle}>
                {list.name || 'Unbenannte Liste'}
              </h3>
              <div style={styles.listMeta}>
                <span>👤 {getPersonName(list.assignedTo)}</span>
                <span style={{ color: priority.color }}>{priority.label}</span>
                {list.dueDate && <span>{formatDate(list.dueDate)}</span>}
              </div>
            </div>
            
            <div style={styles.listStats}>
              <div style={styles.listStat}>
                <span style={{...styles.listStatNumber, color: colors.accent}}>
                  {stats.unchecked}
                </span>
                <span style={styles.listStatLabel}>Offen</span>
              </div>
              <div style={styles.listStat}>
                <span style={{...styles.listStatNumber, color: colors.success}}>
                  {stats.checked}
                </span>
                <span style={styles.listStatLabel}>✓</span>
              </div>
            </div>
            
            <button
              style={styles.deleteBtn}
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(`Liste "${list.name}" löschen?`)) {
                  onDeleteList?.(list.id);
                }
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.background = '#FEE2E2';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '0.5';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              🗑️
            </button>
          </div>
        );
      })}
    </div>
  );

  // ============================================
  // MAIN RENDER
  // ============================================
  
  return (
    <div>
      {/* Content - viewMode wird von Shopping.js gesteuert */}
      {viewMode === 'grid' ? renderGridView() : renderListView()}
    </div>
  );
};

export default ListOverview;